/** Formatação (pura) — moeda, competência e fração ideal. */

/** Centavos → BRL (ex.: 223159 → "R$ 2.231,59"). */
export function brl(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Competência {ano, mes} → "MM/AAAA". */
export function mesAno(c: { ano: number; mes: number }): string {
  return `${String(c.mes).padStart(2, '0')}/${c.ano}`;
}

const FRACAO_CASAS = 6;
const FRACAO_ESCALA = 10 ** FRACAO_CASAS;

/** Fração ideal em micro-unidades → texto com 6 casas (ex.: 131968 → "0,131968"). */
export function fracao(micro: number): string {
  const inteiro = Math.trunc(micro / FRACAO_ESCALA);
  const casas = String(micro % FRACAO_ESCALA).padStart(FRACAO_CASAS, '0');
  return `${inteiro},${casas}`;
}

/**
 * Texto digitado → micro-unidades inteiras (ex.: "0,131968" ou "0.131968" → 131968),
 * sem passar por float. `null` quando não é um decimal não negativo com até 6 casas.
 * A faixa válida (0 < fração ≤ 1) é validada pela API (`invalid_fracao`).
 */
export function parseFracao(texto: string): number | null {
  const m = /^(\d*)(?:[.,](\d{1,6}))?$/.exec(texto.trim());
  if (!m || (m[1] === '' && m[2] === undefined)) return null;
  const inteiro = Number(m[1] || '0');
  const casas = Number((m[2] ?? '').padEnd(FRACAO_CASAS, '0'));
  const micro = inteiro * FRACAO_ESCALA + casas;
  return Number.isSafeInteger(micro) ? micro : null;
}

/** `AAAA-MM-DD` (input type=date / API) → `DD/MM/AAAA`, sem passar por Date (UTC). */
export function dataBr(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : iso;
}

/**
 * Texto digitado em reais → centavos inteiros, sem float ("2.231,59" ou "2231,59" →
 * 223159; "10.50" também vale). Ponto seguido de exatamente 3 dígitos é milhar
 * ("10.000" = R$ 10.000,00); com 1 ou 2 dígitos é decimal ("12.34"). `null` quando vazio,
 * negativo ou fora do formato; o intervalo aceito (0 a R$ 10.000.000,00) é validado pela
 * API (`invalid_valor`).
 */
export function parseReais(texto: string): number | null {
  const t = texto.replace(/^\s*R\$\s*/i, '').trim();
  const comVirgula = /^(\d{1,3}(?:\.\d{3})+|\d+)?,(\d{1,2})$/.exec(t);
  const semDecimal = /^(\d{1,3}(?:\.\d{3})+|\d+)$/.exec(t);
  const comPonto = /^(\d+)\.(\d{1,2})$/.exec(t);
  const [inteiro, decimais] = comVirgula
    ? [comVirgula[1] ?? '0', comVirgula[2] ?? '']
    : semDecimal
      ? [semDecimal[1] ?? '0', '']
      : comPonto
        ? [comPonto[1] ?? '0', comPonto[2] ?? '']
        : [null, ''];
  if (inteiro === null) return null;
  const centavos = Number(inteiro.replaceAll('.', '')) * 100 + Number(decimais.padEnd(2, '0'));
  return Number.isSafeInteger(centavos) ? centavos : null;
}

/** Centavos → texto para o campo de edição, sem separador de milhar (223159 → "2231,59"). */
export function reaisParaCampo(centavos: number): string {
  return `${Math.trunc(centavos / 100)},${String(centavos % 100).padStart(2, '0')}`;
}

const MILI_CASAS = 3;
const MILI_ESCALA = 10 ** MILI_CASAS;

/** Milésimos de m³ → texto com 3 casas, sem separador de milhar (3500 → "3,500"). */
export function mili(valor: number): string {
  const inteiro = Math.trunc(valor / MILI_ESCALA);
  return `${inteiro},${String(valor % MILI_ESCALA).padStart(MILI_CASAS, '0')}`;
}

/**
 * Leitura digitada em m³ → milésimos inteiros, sem float ("12,345" ou "12.345" → 12345).
 * Vírgula e ponto são sempre decimais (até 3 casas); não há separador de milhar. `null`
 * quando vazio, negativo ou fora do formato; o intervalo é validado pela API
 * (`invalid_leitura`).
 */
export function parseMili(texto: string): number | null {
  const m = /^(\d*)(?:[.,](\d{1,3}))?$/.exec(texto.trim());
  if (!m || (m[1] === '' && m[2] === undefined)) return null;
  const valor = Number(m[1] || '0') * MILI_ESCALA + Number((m[2] ?? '').padEnd(MILI_CASAS, '0'));
  return Number.isSafeInteger(valor) ? valor : null;
}

/** Tamanho de arquivo legível, com vírgula decimal (2048 → "2,0 KB", 1572864 → "1,5 MB"). */
export function tamanhoArquivo(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  // Compara o valor já arredondado: 1.048.575 B é "1,0 MB", não "1024,0 KB".
  const kb = Math.round((bytes / 1_024) * 10) / 10;
  if (kb < 1_024) return `${kb.toFixed(1).replace('.', ',')} KB`;
  return `${(bytes / 1_048_576).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Data de um instante UTC no fuso de Brasília: o do SQLite (`AAAA-MM-DD HH:MM:SS`, de
 * `datetime('now')`) ou o ISO com `Z` (`toISOString()`). 01:30 UTC de 1º/07 é 30/06 às
 * 22:30 e aparece como `30/06/2026`.
 */
export function dataUtcBr(utc: string): string {
  const instante = new Date(utc.endsWith('Z') ? utc : `${utc.replace(' ', 'T')}Z`);
  if (Number.isNaN(instante.getTime())) return dataBr(utc);
  return instante.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}
